import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronIcon,
  IntakeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@/components/catalog/icons';
import { IntakeFormModal } from '@/components/intakes/IntakeFormModal';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, toDateInputValue } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import type { Intake, IntakeListSort, Paginated } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import {
  Button,
  type ColumnFilterOption,
  ColumnManager,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  type FilterableTHProps,
  type FrozenSide,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  type SortDirection,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

const PER_PAGE = 20;
const SKELETON_ROWS = 6;

/*
 * 🔴 SORT IS SERVER-SIDE NOW — 2026-10-09. Every funnel column offers
 * Ascending / Descending, held in the URL as `?sort=` / `?order=` BESIDE
 * `?cycleId=` (every URL write here now preserves the other keys) and sent as
 * `sort` / `order` (members: `INTAKE_SORT_MEMBERS`). No sort chosen = nothing
 * sent, so the server's default order is unchanged. Column filters other than
 * Cycle are still page-scoped.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, the sort half of the paragraph below: "There is no
 * … sort on intakes … ordering is always `id DESC`. So **no column declares a
 * `sortKey`**, which is what stops `FilterableTH` rendering sort controls".*
 *
 * There is no name search and no sort on intakes — `page`, `perPage` and
 * `cycleId` are the only params (measured against `listIntakesQuerySchema` in
 * the backend, not inferred from a 200), and ordering is always `id DESC`. So
 * **no column declares a `sortKey`**, which is what stops `FilterableTH`
 * rendering sort controls at all, and every column but Cycle is page-scoped —
 * permanently, because `cycleId` is the only narrowing the API offers. Intake,
 * Starts, Ends, Created and Last edited are not awaiting wiring; there is no
 * key any of them could send.
 *
 * The cycle filter lives in the URL so the 409 a cycle delete returns can link
 * straight here ("/intakes?cycleId=8") and land on exactly the rows blocking
 * it. The URL stays the single source of truth for the request; the Cycle
 * column filter and the header dropdown are both just ways to write it.
 */

/**
 * Column key → the `/intakes` sort member it orders by.
 *
 * ⚠️ **The Cycle column's KEY is `cycleId` and its MEMBER is `cycle`** — the
 * server orders that one by the cycle's start date, not by the id. Sending the
 * column key as the sort is a 422.
 */
const INTAKE_SORT_MEMBERS = {
  intakeName: 'intakeName',
  cycleId: 'cycle',
  startDate: 'startDate',
  endDate: 'endDate',
  createdAt: 'createdAt',
  modifiedBy: 'modifiedBy',
} satisfies Partial<Record<string, IntakeListSort>>;

const SORT_MEMBER_BY_KEY: Partial<Record<string, IntakeListSort>> = INTAKE_SORT_MEMBERS;
const SORT_MEMBERS: ReadonlySet<string> = new Set(Object.values(INTAKE_SORT_MEMBERS));

/** The URL's sort, or `null` when none (or an unrecognised one) is named. */
function parseSort(params: URLSearchParams): { key: IntakeListSort; direction: SortDirection } | null {
  const raw = params.get('sort');
  if (raw === null || !SORT_MEMBERS.has(raw)) return null;
  return { key: raw as IntakeListSort, direction: params.get('order') === 'desc' ? 'desc' : 'asc' };
}

/**
 * `prev` with `cycleId` set (or removed for `''`), every other key — the sort
 * pair included — left as it was.
 */
function withCycleId(prev: URLSearchParams, cycleId: string): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (cycleId === '') next.delete('cycleId');
  else next.set('cycleId', cycleId);
  return next;
}

/**
 * The row-actions lane. Not a `ColumnSpec` — there is nothing on it to filter —
 * so it is named once here and carried in `COLUMN_KEYS` beside the six that are.
 */
const ACTIONS_KEY = 'actions';

/** The Cycle column, named once because three separate rules turn on it. */
const CYCLE_KEY = 'cycleId';

/**
 * Every column this table renders, in its DECLARED order, and the ONE list the
 * header, the body, the skeleton and the width table all walk.
 *
 * 🔴 **HIDING `cycleId` DOES NOT LOOSEN `?cycleId=`, AND THAT IS WHY IT IS
 * ALLOWED TO BE HIDDEN AT ALL.** The Cycle funnel is the only SERVER-scoped
 * filter on this page, so hiding its column takes a live narrowing's control off
 * the screen — the state this file already calls out as the worst kind, a list
 * that is filtered with nothing on screen saying so. It is safe here for one
 * reason and only that reason: **the "Filter by cycle" `Select` in the panel
 * header is a SECOND, always-mounted control over the same URL key**, at every
 * width, outside the `md` wrapper that hides the column manager — so hiding this
 * column removes a duplicate control, never the only one. The visible footer
 * names the cycle too, for the same reason.
 *
 * ⛔ **So do not move that `Select` behind a breakpoint, into the column
 * manager, or into the funnel.** The moment it is conditional, hiding this
 * column strands `?cycleId=` with no way to clear it but Clear column filters,
 * which is not a control anybody looks for when a list is merely short.
 *
 * ⚠️ **Locking the column instead was tried and rejected, and it stays
 * rejected** — but ⚠️ *SUPERSEDED, the reason given: "`ColumnManager` words a
 * locked checkbox 'This column holds the row actions and cannot be hidden', so a
 * locked Cycle column would state something false about itself."* That was true
 * of a fixed sentence; **`ColumnManager` takes `lockReasonOf` now**, so a locked
 * Cycle column could be given a truthful one and this argument no longer
 * decides anything. **The reason that survives is the one above**: the header
 * `Select` makes this column's funnel a duplicate rather than the only control,
 * so there is nothing to protect by locking it, and locking it would take away a
 * reordering the reader is entitled to.
 */
const COLUMN_KEYS: readonly string[] = [
  'intakeName',
  CYCLE_KEY,
  'startDate',
  'endDate',
  'createdAt',
  'modifiedBy',
  ACTIONS_KEY,
];

/**
 * Actions may never be hidden or moved.
 *
 * ⚠️ **Module-level, not an inline literal.** `useColumnLayout` memoises the
 * locked set on the array's identity, so a fresh `['actions']` every render
 * would rebuild it every render.
 */
const LOCKED_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * Keys this table renders with `pinned` rather than with `frozen`.
 *
 * ⚠️ **NOT the same set as `LOCKED_KEYS`, even though they hold the same one key
 * here.** Locked is about hiding and reordering; pinned is about which edge a
 * cell already occupies. They coincide on this table because its only locked
 * column is also its only pinned one — on a page that locks its identifying
 * column too they come apart, and conflating them makes that column unfreezable.
 *
 * It is what tells `useFrozenOffsets` that the right edge is already taken:
 * `pinned` is `sticky right-0` and appears in no freeze list, so unmeasured the
 * first right-frozen column is handed offset 0 and the two stack, one hidden
 * behind the other with nothing erroring. It also withdraws the freeze buttons
 * from this row in `ColumnManager`, because that edge is not the reader's to
 * choose.
 *
 * ⚠️ **Module-level for the same reason as `LOCKED_KEYS`** — both hooks memoise
 * the set on the array's identity, so a fresh literal every render rebuilds it
 * every render.
 */
const PINNED_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * The px each column contributes to the table's minimum width.
 *
 * 🔴 **THIS REPLACES A 1040px `min-w-` LITERAL WRITTEN OUT THREE TIMES, AND THE
 * REASON IT HAD TO IS THE POINT: a fixed floor does not shrink when a column is
 * hidden.** Hide four of seven and the table still claims 1040px, so the
 * survivors are stretched across a scroller that still scrolls — the reader
 * hides columns to fit the table on screen and it does not get any narrower.
 *
 * ⛔ **The sum goes through `style`, NEVER a `min-w-[…]` class.** Tailwind v4
 * scans SOURCE TEXT, so a computed class emits no rule at all: the attribute
 * lands in the DOM, nothing applies it, and the table collapses to its content
 * width with no error anywhere.
 *
 * ⚠️ *SUPERSEDED: "`Table` takes no `style` prop, so the number sits on a block
 * wrapper inside the scroller and the table's own `w-full` fills it."* **`Table`
 * takes `style` now**, so the figure goes straight onto the table and the spacer
 * `<div>` is gone. Only the wrapper moved; the Tailwind rule above is why this is
 * a number in the first place and is untouched.
 *
 * The figures are the `w-*` classes below in px (`w-44` = 176, `w-36` = 144,
 * `w-28` = 112), with the fluid Intake column carrying the remainder — so with
 * every column shown they still add up to the 1040 this table has always used.
 */
const COLUMN_WIDTHS: Record<string, number> = {
  intakeName: 144,
  [CYCLE_KEY]: 176,
  startDate: 144,
  endDate: 144,
  createdAt: 144,
  modifiedBy: 176,
  [ACTIONS_KEY]: 112,
};

/** Header width classes — static literals, so Tailwind emits them. */
const HEADER_CLASS: Record<string, string> = {
  [CYCLE_KEY]: 'w-44',
  startDate: 'w-36',
  endDate: 'w-36',
  createdAt: 'w-36',
  modifiedBy: 'w-44',
};

const BODY_CLASS: Record<string, string> = {
  [CYCLE_KEY]: 'text-xs text-fg-muted',
  startDate: 'text-xs whitespace-nowrap text-fg-muted tabular',
  endDate: 'text-xs whitespace-nowrap text-fg-muted tabular',
  createdAt: 'text-xs whitespace-nowrap text-fg-muted',
};

const SKELETON_CLASS: Record<string, string> = {
  intakeName: 'h-3 w-36',
  [CYCLE_KEY]: 'h-3 w-24',
  startDate: 'h-3 w-24',
  endDate: 'h-3 w-24',
  createdAt: 'h-3 w-24',
  modifiedBy: 'h-3 w-28',
};

/**
 * Reads `?cycleId=` — one id or a comma-separated list (owner 2026-10-28) —
 * keeping only positive integers, de-duplicated, as canonical CSV ('' = none).
 * An old single-id bookmark reads back unchanged.
 *
 * *SUPERSEDED 2026-10-28: "`cycleId` takes exactly one id", and the
 * `singleValued` wrapper that collapsed the funnel to its latest tick.* The
 * column is `multi` now and every tick is sent.
 */
function parseCycleIds(raw: string | null): string {
  if (!raw) return '';
  const ids = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => /^[1-9]\d*$/.test(part));
  return [...new Set(ids)].join(',');
}

/**
 * `modifiedAt` on an intake is NOT NULL and equals `createdAt` on a brand-new
 * row, so it cannot answer "has this been edited". `modifiedBy === null` can,
 * and is the signal used across all three catalog resources.
 */
function EditedCell({ intake }: { intake: Intake }) {
  if (intake.modifiedBy === null) {
    return <span className="text-xs text-fg-subtle">Never edited</span>;
  }
  return (
    <span className="text-xs text-fg-muted">
      {formatDateOnly(intake.modifiedAt)}
      <span className="text-fg-subtle"> by {intake.modifiedBy}</span>
    </span>
  );
}

/**
 * Everything a row renderer needs to place a frozen cell, passed as one object
 * so the header, the body and the skeleton cannot drift on which column is
 * frozen or how far in it sits.
 */
interface FrozenModel {
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}

/**
 * ⚠️ **Returns an EMPTY object for an unfrozen column, not `frozen: undefined`
 * with an offset beside it.** `TH`/`TD` only emit a `left`/`right` when `frozen`
 * is set, and an unfrozen cell that became a positioned one would paint over the
 * table's collapsed borders for no reason at all.
 */
function frozenPropsFor(
  key: string,
  { frozenSideOf, frozenOffsets }: FrozenModel,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const frozen = frozenSideOf(key);
  if (frozen === undefined) return {};
  return { frozen, frozenOffset: frozenOffsets[key] ?? 0 };
}

/*
 * ⚠️ *SUPERSEDED: a local `HeadCell`, which re-implemented `FilterableTH`'s body
 * so that a frozen header could hand `frozen`/`frozenOffset` to `TH` — the two
 * props `FilterableTHProps` did not carry at the time. `CyclesPage` carried an
 * identical copy.* **`FilterableTH` forwards both now**, so both copies are
 * deleted and the shared component is used directly. `aria-sort` is unchanged by
 * the deletion: the branch that computed it here was byte-identical to the one
 * in `ColumnFilter.tsx`, which is the whole reason a second copy was worth
 * removing rather than keeping in step.
 */

function IntakesTableHead({
  columnOrder,
  showActions,
  headerFor,
  headRowRef,
  frozen,
}: {
  columnOrder: readonly string[];
  showActions: boolean;
  headerFor: (key: string) => FilterableTHProps;
  headRowRef: React.RefObject<HTMLTableRowElement | null>;
  frozen: FrozenModel;
}) {
  return (
    <THead>
      {/*
        The row has to be the measured element because a frozen column's offset
        is the summed RENDERED width of the columns outside it, and this table
        is auto layout — a `w-*` on a `<th>` is a request its content can
        outvote, so the widths are knowable only from the DOM.

        ⚠️ *SUPERSEDED: the ref went through `{...({ ref } as object)}`, because
        `TRProps` extended `HTMLAttributes`, which carries no `ref`.* **`TRProps`
        declares `ref` now**, so the cast is gone and a wrong ref type is a build
        error again rather than something `as object` waves through.
      */}
      <TR ref={headRowRef}>
        {columnOrder.map((key) =>
          key === ACTIONS_KEY ? (
            /* Pinned to the right edge of the scroller. This table is 1040px
               wide against a content area of the viewport minus the ~256px
               sidebar, so unpinned the whole column sat past the edge from 1280
               down — measured on production at 1280, where the buttons were
               still inside the viewport by coordinate yet `elementFromPoint`
               returned the panel above them, which is exactly why presence is
               not visibility.

               ⚠️ It keeps `pinned` rather than becoming `frozen="right"`:
               `Table.tsx` says `pinned` IS the one-column special case of
               freezing at offset zero, and this column is forced last below so
               that stays exactly true. */
            <TH key={key} numeric pinned className={showActions ? 'w-28' : 'w-20'}>
              Actions
            </TH>
          ) : (
            <FilterableTH
              key={key}
              {...headerFor(key)}
              className={HEADER_CLASS[key]}
              {...frozenPropsFor(key, frozen)}
            />
          ),
        )}
      </TR>
    </THead>
  );
}

function TableSkeleton({
  columnOrder,
  showActions,
  frozen,
}: {
  columnOrder: readonly string[];
  showActions: boolean;
  frozen: FrozenModel;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {columnOrder.map((key) =>
            key === ACTIONS_KEY ? (
              <TD key={key} numeric pinned>
                <div className="flex justify-end gap-1.5">
                  {showActions && <Skeleton className="h-6 w-6" />}
                  {showActions && <Skeleton className="h-6 w-6" />}
                </div>
              </TD>
            ) : (
              <TD key={key} {...frozenPropsFor(key, frozen)}>
                <Skeleton className={SKELETON_CLASS[key] ?? 'h-3 w-24'} />
              </TD>
            ),
          )}
        </TR>
      ))}
    </TBody>
  );
}

/** One column's cell body. Keyed the same way as the widths and the header. */
function IntakeCell({
  column,
  row,
  cycleLabel,
}: {
  column: string;
  row: Intake;
  cycleLabel: (id: number) => string;
}) {
  switch (column) {
    case CYCLE_KEY:
      return <>{cycleLabel(row.cycleId)}</>;
    case 'startDate':
      return <>{formatDateOnly(row.startDate)}</>;
    case 'endDate':
      return <>{formatDateOnly(row.endDate)}</>;
    case 'createdAt':
      return (
        <>
          {formatDateOnly(row.createdAt)}
          <span className="block text-2xs text-fg-subtle">by {row.createdBy}</span>
        </>
      );
    case 'modifiedBy':
      return <EditedCell intake={row} />;
    default:
      return null;
  }
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-2.5 w-40" />
          </div>
          <Skeleton className="h-4 w-10" />
        </li>
      ))}
    </ul>
  );
}

export function IntakesPage() {
  const { ability } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  /** Canonical CSV of the selected cycle ids; '' = all cycles. */
  const cycleId = parseCycleIds(searchParams.get('cycleId'));
  const cycleIdList = useMemo(() => (cycleId ? cycleId.split(',').map(Number) : []), [cycleId]);
  const searchKey = searchParams.toString();
  const sort = useMemo(() => parseSort(new URLSearchParams(searchKey)), [searchKey]);
  const sortMember = sort?.key ?? null;
  const sortDirection = sort?.direction ?? null;

  const [result, setResult] = useState<Paginated<Intake> | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** `null` closed, `{}` create, `{ intake }` edit. */
  const [formState, setFormState] = useState<{ intake?: Intake } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Intake | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * The intake response deliberately does not embed its parent cycle, so the
   * name is joined client-side from one shared fetch that also feeds the filter
   * and the form's picker.
   */
  const {
    cycles,
    namesById,
    loading: cyclesLoading,
    failed: cyclesFailed,
  } = useCycleOptions();

  /** A cycle outside the loaded page still gets a label, just not its name. */
  const cycleLabel = useCallback(
    (id: number): string => namesById.get(id) ?? `Cycle #${id}`,
    [namesById],
  );

  /*
   * `Cycle #4` is the same string whether that cycle sits past the loaded page
   * or the cycle fetch never came back — and on a failure it is EVERY row at
   * once, a whole table of bare ids with nothing anywhere saying why. The
   * `cyclesFailed` sentence on the panel-header cycle filter is what separates
   * the two, so the id reads as a fallback rather than as all the cycle was ever
   * called.
   *
   * ⚠️ **THAT SENTENCE MOVED ON 2026-09-13 AND IS ONLY NOW VISIBLE WITHOUT
   * OPENING ANYTHING.** *SUPERSEDED, the wording, which named something that did
   * not exist:* ***"The `cyclesFailed` banner below is what separates the
   * two."*** There was **no banner on this page** — the failure was a `disabled
   * <option>` inside the cycle dropdown, readable only by somebody who had
   * already opened a list that looked empty, which is the one reader who had
   * stopped needing to be told. It is now `Field`'s `error` under that filter,
   * where it is read without any gesture at all. **A comment can lose its
   * referent rather than its accuracy**, and this one had.
   *
   * Deliberately NOT folded into `cycleLabel`: that string is also a column
   * grouping value and the filter's own option text, where a sentence would be
   * wrong. The failure is stated once, at page level, where it belongs.
   */

  const rows = useMemo(() => result?.data ?? [], [result]);

  /**
   * The Cycle funnel's options — from the CYCLES MASTER, not the loaded page.
   *
   * ⚠️ **The whole reason this exists.** A server-scoped column's options are
   * otherwise accumulated out of rows the reader has already seen
   * (`seenServerValues`), so a cycle with no intake on the current page of 20 is
   * a cycle nobody can filter for — and the popover says nothing about the gap.
   * Filtering TO a cycle whose rows are not in front of you is the entire use
   * case; the header dropdown beside this table has always offered every cycle,
   * and the funnel silently offered a subset of them.
   *
   * The list is the one `useCycleOptions` already loaded for the dropdown and
   * the id→name join, so this costs no extra request. `perPage: 100` is the
   * endpoint's ceiling, and `GET /cycles` accepts `page` and `perPage` and
   * NOTHING else — measured against `listCyclesQuerySchema` in the backend, not
   * inferred from a 200 — so there is no narrower request available and no
   * second page is fetched. Cycles carry no `isActive`, so there is no retired
   * row to be excluded and none to label as such: the catalog's cycle list has
   * no deactivation to trap us with, unlike the reference-master funnels.
   *
   * ⚠️ **`hasMore` is loaded and deliberately NOT rendered, and this paragraph
   * used to point at a footnote that no longer exists.** It said "see the
   * footnote in `headerFor`" — that was the popover's scope note, REMOVED from
   * `ColumnFilterPanel` on 2026-09-05 under the owner's helper-text rule, so
   * the sentence referred to nothing. There is now nowhere on screen to say
   * "the master ran past one page", and writing one would be the helper text
   * that ruling forbids. What replaces it is structural rather than textual —
   * the row append below.
   *
   * Order is the master's own (`id DESC` — cycles have no sort server-side) and
   * is used as received: `ColumnSpec.options` is deliberately not alphabetised.
   *
   * ⚠️ **`null` on a failed read, NOT an empty array.** `options` REPLACES the
   * accumulated list entirely, so `[]` here would present "we could not reach
   * the cycles endpoint" as "this table has no cycles" — a closed set asserted
   * on the strength of a request that never came back. Returning `null` omits
   * the key and lets the hook fall back to the values the loaded rows carry,
   * which is strictly more than nothing.
   *
   * ⚠️ **Every cycle the LOADED ROWS name is appended, and that is what makes
   * declaring `options` safe at all.** A declared list REPLACES the accumulated
   * `seenServerValues` outright, so a cycle sitting past the master's page 1
   * would LOSE the checkbox it would have had if nothing were declared: its
   * name is right there in the Cycle cell of a row on screen, and it alone
   * cannot be filtered for. That reads as the funnel being broken, not as the
   * master being long. Appending cannot over-offer — every id here is one the
   * API just returned on a real intake, so `?cycleId=` on it is a request that
   * can match, which is the claim `ColumnSpec.options` requires.
   *
   * A cycle pinned by URL that the master does not list is appended for the
   * same reason — otherwise "/intakes?cycleId=8" for a cycle off the master's
   * page reads as filtered with no way to clear it but Clear column filters.
   * Kept as its own line rather than folded into the row loop, because a deep
   * link that matches nothing returns ZERO rows: there is no row to carry it.
   */
  const cycleFilterOptions = useMemo<ColumnFilterOption[] | null>(() => {
    if (cyclesFailed) return null;
    const options: ColumnFilterOption[] = [];
    const seen = new Set<string>();
    const add = (id: number, label: string) => {
      const value = String(id);
      if (seen.has(value)) return;
      seen.add(value);
      options.push({ value, label });
    };
    // `cycleName` is NOT NULL but `''` is expressible — fall back to the id
    // rather than rendering a checkbox with no words beside it.
    for (const cycle of cycles) {
      add(cycle.id, cycle.cycleName.trim() === '' ? `Cycle #${cycle.id}` : cycle.cycleName);
    }
    // Master order first, then whatever the loaded rows add — `options` is
    // deliberately not alphabetised, so the master's own sequence stays intact
    // and the stragglers follow it rather than being interleaved into it.
    for (const row of rows) add(row.cycleId, cycleLabel(row.cycleId));
    for (const id of cycleIdList) add(id, cycleLabel(id));
    return options;
  }, [cycles, cyclesFailed, cycleIdList, cycleLabel, rows]);

  /*
   * The PANEL-HEADER cycle filter's options — a different list from
   * `cycleFilterOptions` above, which feeds the column funnel. Two lists because
   * they answer two questions: the funnel offers every cycle PRESENT ON THIS
   * PAGE plus the master's, so a tick can never return zero rows; this control
   * is the whole-table filter and offers **the master**, because narrowing it to
   * what happens to be loaded is the accumulate-from-the-page failure — the
   * option you came for is missing precisely when no row on this page carries
   * it.
   *
   * 🔴 **THE PIN IS NOT OPTIONAL, AND IT IS THE ONE THING A `Combobox` FAILS AT
   * SILENTLY.** A `Combobox` whose `value` matches no option falls back to its
   * **placeholder** — so "/intakes?cycleId=8" for a cycle off the master's page
   * would read **"All cycles"** while the table stayed filtered to one. A native
   * `<select>` fails the same way and the old markup pinned a `Cycle #n` option
   * for exactly this reason; the primitive changed on 2026-09-13, the hazard did
   * not. A filtered table under an unfiltered-looking control is this codebase's
   * most-repeated silent failure.
   *
   * `cycleName` is NOT NULL but `''` is expressible, so a blank name falls back
   * to the id — the same fallback `cycleFilterOptions` already makes, and more
   * load-bearing here: a `Combobox` row with no words is invisible in the list
   * AND unreachable by typing, where a blank `<option>` was at least clickable.
   */
  const cyclePickerOptions = useMemo<ComboboxOption[]>(() => {
    const options: ComboboxOption[] = cycles.map((cycle) => ({
      value: String(cycle.id),
      label: cycle.cycleName.trim() === '' ? `Cycle #${cycle.id}` : cycle.cycleName,
    }));
    for (const id of cycleIdList) {
      if (!namesById.has(id)) options.push({ value: String(id), label: `Cycle #${id}` });
    }
    return options;
  }, [cycles, cycleIdList, namesById]);

  /*
   * Dates are grouped and labelled by their bare 'YYYY-MM-DD' form rather than
   * a localised one: the option list sorts by label and ISO-8601 is the one
   * date format that sorts chronologically as plain text. It also keeps these
   * values strings end to end, which is the rule for the catalog —
   * `new Date('2026-05-31')` is midnight UTC and renders as 30 May west of
   * Greenwich.
   */
  const columns = useMemo<ColumnSpec<Intake>[]>(
    () => [
      { key: 'intakeName', label: 'Intake', get: (row) => row.intakeName },
      {
        key: 'cycleId',
        label: 'Cycle',
        // The one server-backed column on this page.
        scope: 'server',
        param: 'cycleId',
        // Several ticks go out as one CSV `?cycleId=` (owner 2026-10-28).
        multi: true,
        get: (row) => String(row.cycleId),
        format: (value) => cycleLabel(Number(value)),
        // Omitted rather than passed empty when the master read failed — see
        // `cycleFilterOptions`. The spread is what keeps the key absent.
        ...(cycleFilterOptions ? { options: cycleFilterOptions } : {}),
      },
      { key: 'startDate', label: 'Starts', get: (row) => row.startDate },
      { key: 'endDate', label: 'Ends', get: (row) => row.endDate },
      {
        key: 'createdAt',
        label: 'Created',
        // Grouped by day: `createdAt` is a real timestamp, so full precision
        // would make every row its own distinct value.
        get: (row) => toDateInputValue(row.createdAt),
      },
      {
        key: 'modifiedBy',
        label: 'Last edited',
        /*
         * `modifiedBy` rather than `modifiedAt`, so "Is empty" means exactly
         * "never edited since insert". On intakes `modifiedAt` is NOT NULL and
         * equals `createdAt` on a new row, so it cannot answer that at all.
         */
        get: (row) => row.modifiedBy,
      },
    ],
    [cycleLabel, cycleFilterOptions],
  );

  const filters = useTableFilters(rows, columns);
  const cycleFilter = filters.headerFor(CYCLE_KEY).filter;
  const filterCycleId = parseCycleIds(filters.serverParams.cycleId ?? null);

  const columnLayout = useColumnLayout('intakes', COLUMN_KEYS, LOCKED_KEYS, PINNED_KEYS);
  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /**
   * The column manager's label for a key — read off `columns` rather than
   * written out a second time, so the popover and the header can never disagree
   * about what a column is called.
   */
  const labelOf = useCallback(
    (key: string): string =>
      key === ACTIONS_KEY ? 'Actions' : (columns.find((spec) => spec.key === key)?.label ?? key),
    [columns],
  );

  /**
   * The visible columns in render order, with the actions lane forced last.
   *
   * ⚠️ **`ColumnManager` locks hiding and MOVING a locked key, but it does not
   * stop another column being moved PAST one** — "Move down" on the
   * second-to-last row is enabled, and one more press puts that column after
   * Actions. Actions carries `pinned`, which is hard-coded `right-0`, so it
   * would then paint over whatever had overtaken it. Pinning the lane to the end
   * of the render order keeps `pinned` exactly equal to `frozen="right"` at
   * offset zero, which is the identity `Table.tsx` states.
   */
  const columnOrder = useMemo(
    () => [...columnLayout.visibleKeys.filter((key) => key !== ACTIONS_KEY), ACTIONS_KEY],
    [columnLayout.visibleKeys],
  );

  /*
   * ⚠️ *SUPERSEDED: a local `frozenSideOf` that reported Actions as right-frozen
   * whatever the stored layout said, so the right-hand stack counted the pinned
   * lane and a second right-frozen column did not compute offset zero on top of
   * it.* **`useFrozenOffsets` takes `pinnedKeys` and does exactly that
   * internally**, so the wrapper is gone and the layout's own answer is passed
   * through untouched.
   *
   * The reason it existed is worth keeping, because nothing on screen reports
   * it: two cells sharing the right edge are each fully opaque, so one simply
   * disappears behind the other and the table reads as having lost a column.
   * That hazard now lives in the hook — see `PINNED_KEYS`.
   *
   * Rendering never asks about the actions lane anyway: the header, the body and
   * the skeleton all branch on `ACTIONS_KEY` before reaching `frozenPropsFor`,
   * and that lane keeps `pinned` rather than becoming `frozen="right"`.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    columnOrder,
    columnLayout.frozenSideOf,
    PINNED_KEYS,
  );

  const frozen = useMemo<FrozenModel>(
    () => ({ frozenSideOf: columnLayout.frozenSideOf, frozenOffsets }),
    [columnLayout.frozenSideOf, frozenOffsets],
  );

  /** See `COLUMN_WIDTHS` — a number, never a computed `min-w-[…]` class. */
  const minWidth = useMemo(
    () => columnOrder.reduce((sum, key) => sum + (COLUMN_WIDTHS[key] ?? 0), 0),
    [columnOrder],
  );

  /*
   * The hook's headers, with the Cycle column's two master-shaped states added.
   * Not memoised: the hook returns a fresh object each render anyway, and this
   * is only ever called during render.
   */
  const baseHeaderFor = (key: string): FilterableTHProps => {
    if (key !== CYCLE_KEY) return filters.headerFor(key);
    const props = filters.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;
    return {
      ...props,
      filter: {
        ...filter,
        // `options` is empty until the master lands; without this the popover
        // would read as "No values" for a moment rather than as loading.
        loading: cyclesLoading,
      },
    };
  };

  /**
   * The headers above with the URL-backed server sort laid over every funnel
   * column. ⚠️ The Cycle column's KEY is `cycleId` and its sort MEMBER is
   * `cycle` — see `INTAKE_SORT_MEMBERS`.
   */
  const headerFor = (key: string): FilterableTHProps => {
    const props = baseHeaderFor(key);
    const member = SORT_MEMBER_BY_KEY[key];
    if (member === undefined || props.filter === undefined) return props;
    return {
      ...props,
      filter: {
        ...props.filter,
        sort: {
          direction: sortMember === member ? sortDirection : null,
          onSort: (direction) => changeSort(member, direction),
        },
      },
    };
  };

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listIntakes(
          {
            page,
            perPage: PER_PAGE,
            // Omitted entirely when absent: `?cycleId=` is a 422, not "no filter".
            ...(cycleId ? { cycleId } : {}),
            // Omitted when no sort is chosen, so the server's default order stands.
            ...(sortMember !== null && sortDirection !== null
              ? { sort: sortMember, order: sortDirection }
              : {}),
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load intakes');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, cycleId, sortMember, sortDirection],
  );

  /*
   * No debounce delay: nothing here is typed, so the triggers are paging and
   * the cycle filter. The fetch is still deferred a tick, because `load` flips
   * the loading flag synchronously and doing that in the effect body cascades
   * a render.
   */
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  /*
   * Keeps the Cycle column filter and the URL in step, in whichever direction
   * moved. Whoever changed wins; when neither did and they still disagree — a
   * fresh mount on "?cycleId=8" — the URL wins, so a deep link populates the
   * control instead of being wiped by it. Both branches converge on the next
   * render, so this settles rather than ping-pongs.
   */
  const previousFilterCycleId = useRef(filterCycleId);
  useEffect(() => {
    const filterMoved = previousFilterCycleId.current !== filterCycleId;
    previousFilterCycleId.current = filterCycleId;
    if (filterCycleId === cycleId) return;
    if (filterMoved) {
      setPage(1);
      setSearchParams((prev) => withCycleId(prev, filterCycleId), { replace: true });
    } else {
      cycleFilter?.onChange({
        selected: cycleIdList.map(String),
        emptiness: 'any',
      });
    }
  }, [cycleId, cycleIdList, filterCycleId, cycleFilter, setSearchParams]);

  /*
   * Intakes are admin-only to write, and this is enforced server-side however
   * generous the permission set looks — the router carries
   * `requireRole(...ADMIN_ROLES)`, so an `advanced_user` holding
   * `intakes.create` still gets a 403. Asking `ability` keeps that a single
   * call site for the day enforcement moves to the permission model.
   */
  const canCreate = ability.canCreate('intakes');
  const canUpdate = ability.canUpdate('intakes');
  const canDelete = ability.canDelete('intakes');
  const showActions = canUpdate || canDelete;

  /**
   * Why the one locked column cannot be hidden, in the reader's terms.
   *
   * ⚠️ **It has to follow `showActions`, and on this table that is the COMMON
   * branch rather than the edge.** Intakes are admin-only to write, so for a
   * viewer, a `basic_user` and an `advanced_user` alike the lane renders a dash
   * and holds no controls at all — naming Edit and Delete to those readers would
   * describe buttons they cannot see. The fallback `ColumnManager` supplies is a
   * bare "cannot be hidden", which states the refusal without the reason.
   *
   * This is a **why-this-is-disabled** sentence, which is one of the four kinds
   * the no-helper-text rule keeps: without it a locked checkbox and a broken one
   * are the same thing on screen.
   *
   * ⚠️ **Cycle is deliberately NOT locked and so gets no sentence here** — see
   * `COLUMN_KEYS` for why hiding it is safe.
   */
  const lockReasonOf = useCallback(
    (key: string): string | undefined => {
      if (key !== ACTIONS_KEY) return undefined;
      return showActions
        ? 'Actions holds the Edit and Delete controls for each row, so it cannot be hidden'
        : 'Actions is the per-row control lane, so it is kept even where you have no controls on these rows';
    },
    [showActions],
  );

  const visibleRows = filters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  function changeCycle(next: string) {
    setPage(1);
    setSearchParams((prev) => withCycleId(prev, next), { replace: true });
  }

  /** Writes the sort into the URL beside `cycleId` and returns to page 1. */
  function changeSort(member: IntakeListSort, direction: SortDirection) {
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sort', member);
        next.set('order', direction);
        return next;
      },
      { replace: true },
    );
  }

  function clearFilters() {
    filters.clearAll();
    changeCycle('');
  }

  function openDelete(intake: Intake) {
    setDeleteError(null);
    setPendingDelete(intake);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteIntake(pendingDelete.id);
      setPendingDelete(null);
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone — the outcome the user wanted, so treat it as success.
        setPendingDelete(null);
        if (rows.length === 1 && page > 1) setPage((p) => p - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 403) {
        setDeleteError(
          `${denialReason(ability, 'delete', 'intakes') ?? 'The server refused that delete.'} Nothing was deleted — ask an administrator.`,
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  function handleSaved() {
    const wasCreate = !formState?.intake;
    setFormState(null);
    if (wasCreate && page !== 1) setPage(1);
    else void load();
  }

  const scopeLabel =
    cycleIdList.length > 0 ? ` in ${cycleIdList.map((id) => cycleLabel(id)).join(', ')}` : '';
  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'intake' : 'intakes'}${scopeLabel}`;
  const hiddenLabel =
    hiddenByFilters > 0 ? ` — ${hiddenByFilters} hidden by column filters on this page` : '';

  const newIntakeButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
      New intake
    </Button>
  ) : null;

  const emptyState =
    rows.length === 0 ? (
      <IntakesEmptyState
        filtered={cycleId !== ''}
        onClear={clearFilters}
        hasError={Boolean(error)}
        action={newIntakeButton}
      />
    ) : (
      <PageFilterEmptyState pageRows={rows.length} onClear={filters.clearAll} />
    );

  return (
    <>
      <PageHeader
        eyebrow="Catalog"
        title="Intakes"
        actions={newIntakeButton}
      />

      <Panel>
        <PanelHeader
          title="All intakes"
          actions={
            /* `PanelHeader`'s actions slot is `shrink-0` and cannot wrap, so the
               two controls get their own wrapping row inside it. */
            <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
              {/* ⛔ NOT behind a breakpoint, and NOT inside the column manager.
                  This is the second control over `?cycleId=`, and it is what
                  makes the Cycle COLUMN safe to hide — see `COLUMN_KEYS`. */}
              <div className="w-full sm:w-56">
                {/*
                  * ⚠️ **A `Combobox` SINCE 2026-09-13 — owner's instruction that
                  * every non-table-column filter is searchable, regardless of
                  * option count.** Eight cycles today, up to the 100 the master
                  * fetch asks for; typing beats scrolling well before that.
                  *
                  * 🔴 **THE FAILED-READ STATE MOVED OUT OF THE LIST AND ONTO THE
                  * FIELD, BECAUSE A `Combobox` CANNOT CARRY IT.** *SUPERSEDED,
                  * the markup and its reasoning:* ***"An empty dropdown is the
                  * failure's most convincing disguise — it reads as 'there are
                  * no cycles', so the control says which state it is in"***, as
                  * a `<option value="__cycles-unavailable" disabled>`.
                  * **`ComboboxOption` has no disabled member and must not grow
                  * one** — every row in that listbox is selectable by Enter, so
                  * a decorative row would be a value somebody can pick, and
                  * `?cycleId=__cycles-unavailable` is a filter matching nothing.
                  *
                  * ⚠️ **The claim the old option made is UNCHANGED and is now
                  * made somewhere better**: `Field`'s `error` is visible without
                  * opening the control, where the disabled option could only be
                  * read by somebody who had already opened a list that looked
                  * empty. `invalid` marks the trigger so the two agree.
                  * ⚠️ **This is the sentence the block comment ~450 lines up
                  * calls "the `cyclesFailed` banner below"** — until today there
                  * was no such banner anywhere on this page, only that hidden
                  * option, so that comment describes reality for the first time.
                  * It is a REFUSAL, not helper text: it names a state the reader
                  * cannot otherwise see, and a sweep must not delete it.
                  *
                  * ⚠️ **`disabled` while loading is KEPT and `loading` is NOT
                  * passed.** They are the same boolean here, so `Combobox`'s own
                  * loading row could never be seen — a prop that cannot render
                  * is dead code that reads as a feature. The loading STATE is
                  * carried by `placeholder`, which is the trigger text, and is
                  * therefore the exact string the old empty `<option>` showed.
                  */}
                <Field
                  label="Filter by cycle"
                  hideLabel
                  {...(cyclesFailed ? { error: 'Cycle list unavailable.' } : {})}
                >
                  {({ id }) => (
                    /* Several cycles at once (owner 2026-10-28); none ticked
                       is "All cycles". */
                    <MultiCombobox
                      id={id}
                      values={cycleIdList.map(String)}
                      onChange={(values) => changeCycle(values.join(','))}
                      options={cyclePickerOptions}
                      disabled={cyclesLoading}
                      placeholder={cyclesLoading ? 'Loading cycles…' : 'All cycles'}
                      searchPlaceholder="Search cycles…"
                      {...(cyclesFailed ? { invalid: true } : {})}
                    />
                  )}
                </Field>
              </div>
              {/* Hidden below `md` because that is where the table itself is
                  replaced by the card list, and a column control over a surface
                  with no columns is an affordance whose only outcome is nothing.
                  A wrapper rather than `hidden md:inline-flex` on the button:
                  `hidden` and the Button's own `inline-flex` are the same CSS
                  property, so which wins would be decided by the emitted
                  stylesheet's order rather than by the order they are written. */}
              <div className="hidden md:block">
                <ColumnManager
                  layout={columnLayout}
                  labelOf={labelOf}
                  lockedKeys={LOCKED_KEYS}
                  lockReasonOf={lockReasonOf}
                  pinnedKeys={PINNED_KEYS}
                />
              </div>
            </div>
          }
        />

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
          {hiddenLabel}
        </p>

        {error && (
          <Notice
            tone="danger"
            role="alert"
            title={<>Couldn&rsquo;t load intakes.</>}
            className="mx-4 my-3"
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          <div className="md:hidden">
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                  <li key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                      {row.intakeName}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-fg-muted">{cycleLabel(row.cycleId)}</p>
                    <p className="mt-1 text-xs text-fg-secondary tabular">
                      {formatDateOnly(row.startDate)} → {formatDateOnly(row.endDate)}
                    </p>
                    <div className="mt-1">
                      <EditedCell intake={row} />
                    </div>
                    {showActions && (
                      <div className="mt-2.5 flex items-center justify-end gap-2">
                        {canUpdate && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<PencilIcon />}
                            onClick={() => setFormState({ intake: row })}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="danger"
                            size="sm"
                            leadingIcon={<TrashIcon />}
                            onClick={() => openDelete(row)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hidden md:block">
            {/*
              🔴 **ONE MOUNT, THREE STATES — this was three separate `<Table>`s.**
              The skeleton, the headers-only empty view and the data table now
              differ only in what follows the shared `<THead>`, and that is
              load-bearing rather than tidy: `useFrozenOffsets` measures the
              header ROW and re-measures through a `ResizeObserver` attached to
              it, so a header that unmounts and remounts when the data lands
              leaves the observer watching a detached row. Offsets would then
              freeze at whatever the SKELETON measured — and the first column is
              fluid, so a skeleton bar and a real intake name genuinely give
              different widths. Keeping one element at one position in the tree
              keeps the same `<tr>` throughout, and the observer live.

              The three states are otherwise unchanged: headers stay mounted
              when column filters have emptied the page, so the filter that did
              it is still reachable to undo, and nothing renders at all when the
              server itself returned no rows.
            */}
            {(initialLoad || rows.length > 0) && (
              <TableWrap frozenHeader>
                {/* The floor is a number because it moves with the visible
                    columns — see `COLUMN_WIDTHS`. It sits on the table itself
                    now; the spacer `<div>` that used to carry it is gone. */}
                <Table style={{ minWidth }}>
                  <IntakesTableHead
                    columnOrder={columnOrder}
                    showActions={showActions}
                    headerFor={headerFor}
                    headRowRef={headRowRef}
                    frozen={frozen}
                  />
                  {initialLoad ? (
                    <TableSkeleton
                      columnOrder={columnOrder}
                      showActions={showActions}
                      frozen={frozen}
                    />
                  ) : visibleRows.length > 0 ? (
                    <TBody>
                      {visibleRows.map((row) => (
                        <TR key={row.id} interactive>
                          {columnOrder.map((key) =>
                            key === 'intakeName' ? (
                              /* Still the row header wherever it sits — the
                                 name is what identifies the row, not its
                                 position. */
                              <TH key={key} scope="row" {...frozenPropsFor(key, frozen)}>
                                <span className="[overflow-wrap:anywhere]">{row.intakeName}</span>
                              </TH>
                            ) : key === ACTIONS_KEY ? (
                              <TD key={key} numeric pinned>
                                <div className="flex justify-end gap-1.5">
                                  {showActions ? (
                                    <>
                                      {canUpdate && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          iconOnly
                                          leadingIcon={<PencilIcon />}
                                          aria-label={`Edit ${row.intakeName}`}
                                          title={`Edit ${row.intakeName}`}
                                          onClick={() => setFormState({ intake: row })}
                                        />
                                      )}
                                      {canDelete && (
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          iconOnly
                                          leadingIcon={<TrashIcon />}
                                          aria-label={`Delete ${row.intakeName}`}
                                          title={`Delete ${row.intakeName}`}
                                          onClick={() => openDelete(row)}
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-xs text-fg-subtle">—</span>
                                  )}
                                </div>
                              </TD>
                            ) : (
                              <TD
                                key={key}
                                className={BODY_CLASS[key]}
                                {...frozenPropsFor(key, frozen)}
                              >
                                <IntakeCell column={key} row={row} cycleLabel={cycleLabel} />
                              </TD>
                            ),
                          )}
                        </TR>
                      ))}
                    </TBody>
                  ) : null}
                </Table>
              </TableWrap>
            )}
            {!initialLoad && visibleRows.length === 0 && emptyState}
          </div>
        </div>

        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No intakes to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
              {/* The cycle is named here as well as in the header `Select`,
                  because the Cycle COLUMN can be hidden and this list would
                  otherwise be narrowed by `?cycleId=` with nothing beneath the
                  table saying so. It states a state the reader cannot otherwise
                  see, which is what keeps it out of the helper-text rule. */}
              {scopeLabel}
              {hiddenByFilters > 0 && (
                <span className="text-fg-subtle"> · {hiddenByFilters} hidden by column filters</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <IntakeFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        intake={formState?.intake}
        cycles={cycles}
        cyclesLoading={cyclesLoading}
        cyclesFailed={cyclesFailed}
        // A default only when the filter names exactly one cycle.
        {...(cycleIdList.length === 1 && cycleIdList[0] !== undefined
          ? { defaultCycleId: cycleIdList[0] }
          : {})}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.intakeName}?` : 'Delete intake?'}
        description="The intake will be deleted and can't be recovered. An intake a project still uses can't be deleted."
        confirmLabel="Delete intake"
        loading={deleting}
        error={deleteError}
      />
    </>
  );
}

/**
 * Distinct from "no intakes": the server returned rows and the column filters —
 * which only ever see the loaded page — hid all of them. Saying so is the whole
 * point; otherwise it reads as an empty cycle.
 */
function PageFilterEmptyState({ pageRows, onClear }: { pageRows: number; onClear: () => void }) {
  return (
    <EmptyState
      icon={<IntakeIcon />}
      title="No rows on this page match the column filters"
      description={`Filters apply to the ${pageRows} rows loaded here. Try another page.`}
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear column filters
        </Button>
      }
    />
  );
}

function IntakesEmptyState({
  filtered,
  onClear,
  hasError,
  action,
}: {
  filtered: boolean;
  onClear: () => void;
  hasError: boolean;
  action: ReactNode;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<IntakeIcon />}
        title="Nothing to show"
        description="The intake list couldn’t be loaded — see the message above."
      />
    );
  }

  return (
    <EmptyState
      icon={<IntakeIcon />}
      title={filtered ? 'No intakes in this cycle' : 'No intakes yet'}
      description={
        filtered
          ? 'None recorded for this cycle yet.'
          : 'Add the first intake window.'
      }
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Show all cycles
          </Button>
        ) : (
          action
        )
      }
    />
  );
}
